import { Group } from '@mantine/core';
import React from 'react';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { BasicButton, IBasicButtonProps } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

export interface IInlineConfirmButtonProps extends IBasicButtonProps {
  confirmButtonProps?: Omit<IBasicButtonProps, 'onClick' | 'loading'>;
  cancelButtonProps?: Omit<IBasicButtonProps, 'onClick'>;
  onConfirmClick: () => void | Promise<void>;
}

const DefaultProps: Partial<IInlineConfirmButtonProps> = {
  size: 'sm',
  variant: 'filled',
  color: 'blue',
};

const DefaultConfirmButtonProps: Partial<IBasicButtonProps> = {
  size: 'sm',
  color: 'red',
  children: 'Confirm',
};

const DefaultCancelButtonProps: Partial<IBasicButtonProps> = {
  size: 'sm',
  color: 'dark',
  variant: 'subtle',
  children: 'Cancel',
};

/** @registry Two-step inline confirm button: click swaps to Confirm/Cancel controls in place instead of opening a modal; use for low-friction destructive/committing actions. */
export const InlineConfirmButton = React.forwardRef<
  HTMLButtonElement,
  IInlineConfirmButtonProps
>(
  (
    {
      children,
      confirmButtonProps,
      cancelButtonProps,
      onConfirmClick,
      ...props
    },
    ref,
  ) => {
    const { opened, loading, requestOrRun, confirm, close } =
      useConfirmableAction(onConfirmClick, true);

    if (opened) {
      return (
        <Group>
          <SecondaryButton
            {...DefaultCancelButtonProps}
            {...cancelButtonProps}
            onClick={close}
            disabled={cancelButtonProps?.disabled || loading}
          />
          <BasicButton
            {...DefaultConfirmButtonProps}
            {...confirmButtonProps}
            loading={loading}
            onClick={() => confirm()}
          />
        </Group>
      );
    }

    return (
      <BasicButton
        {...DefaultProps}
        {...props}
        ref={ref}
        onClick={() => requestOrRun()}
      >
        {children}
      </BasicButton>
    );
  },
);
InlineConfirmButton.displayName = 'InlineConfirmButton';
